import React, { useState, useEffect, useRef } from 'react';
import { Cloud, CloudRain, Sun, Wind, Droplets, Eye, Gauge, CloudSnow, CloudDrizzle, CloudLightning, Calendar, Clock, TrendingUp, MapPin, Heart, AlertTriangle, Sunrise, Sunset, Moon, Volume2, VolumeX, Bell, BellOff, Globe, Palette, Settings as SettingsIcon, X, Download } from 'lucide-react';

export default function WeatherApp() {
  // Core state
  const [location, setLocation] = useState('');
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [viewMode, setViewMode] = useState('current');
  
  // Feature states
  const [favorites, setFavorites] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [language, setLanguage] = useState('en');
  const [theme, setTheme] = useState('cyberpunk');
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Refs
  const canvasRef = useRef(null);
  const audioRef = useRef(null);

  // Translations
  const translations = {
    en: { title: 'AEOLUS', subtitle: 'kairos', search: 'Search', myLocation: 'My Location', current: 'Current', hourly: 'Hourly', daily: '7-Day', humidity: 'Humidity', windSpeed: 'Wind Speed', visibility: 'Visibility', pressure: 'Pressure', uvIndex: 'UV Index', feelsLike: 'Feels Like', sunrise: 'Sunrise', sunset: 'Sunset', moonPhase: 'Moon Phase', settings: 'Settings', theme: 'Theme', language: 'Language', sound: 'Ambient Sound', notifications: 'Notifications', enterLocation: 'Enter location...', searching: 'Scanning...', analyzing: 'Analyzing Data...', highUV: 'High UV Index - Wear sunscreen!', highWind: 'High winds detected - Stay safe!', extremeHeat: 'Extreme heat - Stay hydrated!', freezing: 'Freezing temperatures - Bundle up!', lowVis: 'Low visibility - Drive carefully!', rainChance: 'Rain Probability', tempTrend: '24-Hour Temperature Trend', installApp: 'Install App' },
    es: { title: 'CLIMA', subtitle: 'kairos', search: 'Buscar', myLocation: 'Mi Ubicación', current: 'Actual', hourly: 'Por Hora', daily: '7 Días', humidity: 'Humedad', windSpeed: 'Velocidad del Viento', visibility: 'Visibilidad', pressure: 'Presión', uvIndex: 'Índice UV', feelsLike: 'Sensación Térmica', sunrise: 'Amanecer', sunset: 'Atardecer', moonPhase: 'Fase Lunar', settings: 'Configuración', theme: 'Tema', language: 'Idioma', sound: 'Sonido Ambiente', notifications: 'Notificaciones', enterLocation: 'Ingrese ubicación...', searching: 'Escaneando...', analyzing: 'Analizando Datos...', highUV: '¡Índice UV alto - Use protector solar!', highWind: '¡Vientos fuertes detectados - Manténgase seguro!', extremeHeat: '¡Calor extremo - Manténgase hidratado!', freezing: '¡Temperaturas bajo cero - Abríguese!', lowVis: '¡Baja visibilidad - Conduzca con cuidado!', rainChance: 'Probabilidad de Lluvia', tempTrend: 'Tendencia de Temperatura 24H', installApp: 'Instalar App' },
    fr: { title: 'MÉTÉO', subtitle: 'kairos', search: 'Rechercher', myLocation: 'Ma Position', current: 'Actuel', hourly: 'Horaire', daily: '7 Jours', humidity: 'Humidité', windSpeed: 'Vitesse du Vent', visibility: 'Visibilité', pressure: 'Pression', uvIndex: 'Indice UV', feelsLike: 'Ressenti', sunrise: 'Lever du Soleil', sunset: 'Coucher du Soleil', moonPhase: 'Phase Lunaire', settings: 'Paramètres', theme: 'Thème', language: 'Langue', sound: 'Son Ambiant', notifications: 'Notifications', enterLocation: 'Entrez un lieu...', searching: 'Analyse...', analyzing: 'Analyse des Données...', highUV: 'Indice UV élevé - Portez de la crème solaire!', highWind: 'Vents forts détectés - Restez en sécurité!', extremeHeat: 'Chaleur extrême - Restez hydraté!', freezing: 'Températures glaciales - Couvrez-vous!', lowVis: 'Faible visibilité - Conduisez prudemment!', rainChance: 'Probabilité de Pluie', tempTrend: 'Tendance de Température 24H', installApp: 'Installer l\'App' },
    de: { title: 'WETTER', subtitle: 'kairos', search: 'Suchen', myLocation: 'Mein Standort', current: 'Aktuell', hourly: 'Stündlich', daily: '7 Tage', humidity: 'Luftfeuchtigkeit', windSpeed: 'Windgeschwindigkeit', visibility: 'Sichtweite', pressure: 'Luftdruck', uvIndex: 'UV-Index', feelsLike: 'Gefühlt', sunrise: 'Sonnenaufgang', sunset: 'Sonnenuntergang', moonPhase: 'Mondphase', settings: 'Einstellungen', theme: 'Design', language: 'Sprache', sound: 'Umgebungsgeräusche', notifications: 'Benachrichtigungen', enterLocation: 'Ort eingeben...', searching: 'Scannen...', analyzing: 'Daten analysieren...', highUV: 'Hoher UV-Index - Sonnenschutz verwenden!', highWind: 'Starker Wind erkannt - Bleiben Sie sicher!', extremeHeat: 'Extreme Hitze - Bleiben Sie hydriert!', freezing: 'Gefriertemperaturen - Warm anziehen!', lowVis: 'Geringe Sicht - Vorsichtig fahren!', rainChance: 'Regenwahrscheinlichkeit', tempTrend: '24-Stunden-Temperaturtrend', installApp: 'App Installieren' }
  };

  const t = translations[language];

  // Theme configurations
  const themes = {
    cyberpunk: { primary: '#00fff2', secondary: '#7b2ff7', accent: '#ff00ff', background: 'linear-gradient(135deg, #05080f 0%, #0d1520 50%, #141a2e 100%)', cardBg: 'rgba(0, 0, 0, 0.6)', textPrimary: '#00fff2', textSecondary: '#a78bfa' },
    nature: { primary: '#4ade80', secondary: '#22c55e', accent: '#86efac', background: 'linear-gradient(135deg, #064e3b 0%, #065f46 50%, #047857 100%)', cardBg: 'rgba(6, 78, 59, 0.6)', textPrimary: '#86efac', textSecondary: '#4ade80' },
    ocean: { primary: '#06b6d4', secondary: '#0891b2', accent: '#22d3ee', background: 'linear-gradient(135deg, #082f49 0%, #0c4a6e 50%, #075985 100%)', cardBg: 'rgba(8, 47, 73, 0.6)', textPrimary: '#22d3ee', textSecondary: '#67e8f9' },
    sunset: { primary: '#fb923c', secondary: '#f97316', accent: '#fdba74', background: 'linear-gradient(135deg, #7c2d12 0%, #9a3412 50%, #c2410c 100%)', cardBg: 'rgba(124, 45, 18, 0.6)', textPrimary: '#fdba74', textSecondary: '#fb923c' }
  };

  const currentTheme = themes[theme];

  // Moon phase calculator
  const getMoonPhase = (date) => {
    const year = date.getFullYear();
    let month = date.getMonth() + 1;
    const day = date.getDate();
    
    let c, e, jd, b;
    let yr = year;
    
    if (month < 3) {
      yr--;
      month += 12;
    }
    
    ++month;
    c = 365.25 * yr;
    e = 30.6 * month;
    jd = c + e + day - 694039.09;
    jd /= 29.5305882;
    b = parseInt(jd);
    jd -= b;
    b = Math.round(jd * 8);
    
    if (b >= 8) b = 0;
    
    const phases = ['🌑', '🌒', '🌓', '🌔', '🌕', '🌖', '🌗', '🌘'];
    const phaseNames = {
      en: ['New Moon', 'Waxing Crescent', 'First Quarter', 'Waxing Gibbous', 'Full Moon', 'Waning Gibbous', 'Last Quarter', 'Waning Crescent'],
      es: ['Luna Nueva', 'Creciente', 'Cuarto Creciente', 'Gibosa Creciente', 'Luna Llena', 'Gibosa Menguante', 'Cuarto Menguante', 'Menguante'],
      fr: ['Nouvelle Lune', 'Premier Croissant', 'Premier Quartier', 'Gibbeuse Croissante', 'Pleine Lune', 'Gibbeuse Décroissante', 'Dernier Quartier', 'Dernier Croissant'],
      de: ['Neumond', 'Zunehmende Sichel', 'Erstes Viertel', 'Zunehmender Mond', 'Vollmond', 'Abnehmender Mond', 'Letztes Viertel', 'Abnehmende Sichel']
    };
    
    return { emoji: phases[b], name: phaseNames[language][b] };
  };

  // Request notification permission
  const toggleNotifications = async () => {
    if (!notificationsEnabled && 'Notification' in window) {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        new Notification('Weather Alerts Enabled! 🌦️', {
          body: 'You\'ll receive alerts for severe weather conditions'
        });
      }
    } else {
      setNotificationsEnabled(false);
    }
  };

  // Send notification
  const sendNotification = (title, body) => {
    if (notificationsEnabled && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body });
    }
  };

  // PWA Install
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const installPWA = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          setShowInstall(false);
        }
        setDeferredPrompt(null);
      });
    }
  };

  // Clock update
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Load preferences
  useEffect(() => {
    const saved = localStorage.getItem('weatherFavorites');
    if (saved) setFavorites(JSON.parse(saved));
    
    const savedLang = localStorage.getItem('weatherLanguage');
    if (savedLang) setLanguage(savedLang);
    
    const savedTheme = localStorage.getItem('weatherTheme');
    if (savedTheme) setTheme(savedTheme);
    
    const savedSound = localStorage.getItem('weatherSound');
    if (savedSound === 'true') setSoundEnabled(true);
    
    const savedNotif = localStorage.getItem('weatherNotifications');
    if (savedNotif === 'true') setNotificationsEnabled(true);
  }, []);

  // Save preferences
  useEffect(() => { localStorage.setItem('weatherLanguage', language); }, [language]);
  useEffect(() => { localStorage.setItem('weatherTheme', theme); }, [theme]);
  useEffect(() => { localStorage.setItem('weatherSound', soundEnabled.toString()); }, [soundEnabled]);
  useEffect(() => { localStorage.setItem('weatherNotifications', notificationsEnabled.toString()); }, [notificationsEnabled]);

  // Ambient sound - Using HTML5 Audio with data URIs for rain effect
  useEffect(() => {
    if (!weather || !soundEnabled) {
      if (audioRef.current) audioRef.current.pause();
      return;
    }

    const condition = weather.current.condition.text.toLowerCase();
    
    if ((condition.includes('rain') || condition.includes('storm')) && audioRef.current) {
      // Create a simple rain-like sound using Web Audio API
      const playRainSound = () => {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        
        const audioContext = new AudioContext();
        const gainNode = audioContext.createGain();
        gainNode.gain.value = 0.1;
        gainNode.connect(audioContext.destination);

        const playDrop = () => {
          const oscillator = audioContext.createOscillator();
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(Math.random() * 1000 + 500, audioContext.currentTime);
          oscillator.connect(gainNode);
          oscillator.start();
          oscillator.stop(audioContext.currentTime + 0.1);
        };

        const interval = setInterval(() => {
          for (let i = 0; i < 3; i++) {
            setTimeout(playDrop, Math.random() * 100);
          }
        }, 200);

        return () => {
          clearInterval(interval);
          audioContext.close();
        };
      };

      const cleanup = playRainSound();
      return cleanup;
    }
  }, [weather, soundEnabled]);

  // Particle system for weather effects
  useEffect(() => {
    if (!weather || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const condition = weather.current.condition.text.toLowerCase();
    
    let particleCount = 0;
    let particleType = 'none';

    if (condition.includes('rain') || condition.includes('drizzle')) {
      particleCount = 150;
      particleType = 'rain';
    } else if (condition.includes('snow')) {
      particleCount = 100;
      particleType = 'snow';
    } else if (condition.includes('thunder') || condition.includes('storm')) {
      particleCount = 200;
      particleType = 'storm';
    }

    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height - canvas.height;
        this.speed = particleType === 'snow' ? Math.random() * 1 + 0.5 : Math.random() * 5 + 5;
        this.size = particleType === 'snow' ? Math.random() * 3 + 2 : Math.random() * 2 + 1;
        this.opacity = Math.random() * 0.5 + 0.3;
      }

      update() {
        this.y += this.speed;
        if (particleType === 'snow') {
          this.x += Math.sin(this.y * 0.01) * 0.5;
        }
        if (this.y > canvas.height) {
          this.reset();
        }
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.opacity;
        
        if (particleType === 'snow') {
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.strokeStyle = currentTheme.primary;
          ctx.lineWidth = this.size;
          ctx.beginPath();
          ctx.moveTo(this.x, this.y);
          ctx.lineTo(this.x, this.y + 10);
          ctx.stroke();
        }
        
        ctx.restore();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      if (particleType === 'storm' && Math.random() > 0.98) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', handleResize);
    };
  }, [weather, currentTheme]);

  // Check for weather alerts
  useEffect(() => {
    if (!weather) return;

    const newAlerts = [];
    
    if (weather.current.uv >= 8) {
      newAlerts.push({ type: 'warning', message: t.highUV, icon: <Sun size={20} /> });
    }
    if (weather.current.wind_kph >= 50) {
      newAlerts.push({ type: 'warning', message: t.highWind, icon: <Wind size={20} /> });
    }
    if (weather.current.temp_c >= 35) {
      newAlerts.push({ type: 'danger', message: t.extremeHeat, icon: <AlertTriangle size={20} /> });
    } else if (weather.current.temp_c <= 0) {
      newAlerts.push({ type: 'danger', message: t.freezing, icon: <CloudSnow size={20} /> });
    }
    if (weather.current.vis_km < 2) {
      newAlerts.push({ type: 'warning', message: t.lowVis, icon: <Eye size={20} /> });
    }

    setAlerts(newAlerts);

    // Send notifications for new alerts
    if (newAlerts.length > 0 && notificationsEnabled) {
      newAlerts.forEach(alert => {
        sendNotification('Weather Alert! ⚠️', alert.message);
      });
    }
  }, [weather, notificationsEnabled]);

  const getWeatherIcon = (condition) => {
    const lowerCondition = condition?.toLowerCase() || '';
    if (lowerCondition.includes('rain')) return <CloudRain className="weather-icon" />;
    if (lowerCondition.includes('snow')) return <CloudSnow className="weather-icon" />;
    if (lowerCondition.includes('drizzle')) return <CloudDrizzle className="weather-icon" />;
    if (lowerCondition.includes('thunder') || lowerCondition.includes('storm')) return <CloudLightning className="weather-icon" />;
    if (lowerCondition.includes('cloud')) return <Cloud className="weather-icon" />;
    if (lowerCondition.includes('clear') || lowerCondition.includes('sun')) return <Sun className="weather-icon" />;
    return <Cloud className="weather-icon" />;
  };

  const getSmallWeatherIcon = (condition) => {
    const lowerCondition = condition?.toLowerCase() || '';
    if (lowerCondition.includes('rain')) return <CloudRain size={32} />;
    if (lowerCondition.includes('snow')) return <CloudSnow size={32} />;
    if (lowerCondition.includes('drizzle')) return <CloudDrizzle size={32} />;
    if (lowerCondition.includes('thunder') || lowerCondition.includes('storm')) return <CloudLightning size={32} />;
    if (lowerCondition.includes('cloud')) return <Cloud size={32} />;
    if (lowerCondition.includes('clear') || lowerCondition.includes('sun')) return <Sun size={32} />;
    return <Cloud size={32} />;
  };

  const getBackgroundStyle = () => {
    if (!weather) return { background: currentTheme.background };

    const condition = weather.current.condition.text.toLowerCase();
    const isDay = weather.current.is_day === 1;

    if (condition.includes('rain') || condition.includes('drizzle')) {
      return { background: isDay ? 'linear-gradient(135deg, #1a2f4a 0%, #2d4a6e 50%, #1a2f4a 100%)' : 'linear-gradient(135deg, #050811 0%, #0f1a2e 50%, #050811 100%)' };
    } else if (condition.includes('snow')) {
      return { background: isDay ? 'linear-gradient(135deg, #c5d8e8 0%, #9ab8d1 50%, #c5d8e8 100%)' : 'linear-gradient(135deg, #111827 0%, #1f2937 50%, #111827 100%)' };
    } else if (condition.includes('thunder') || condition.includes('storm')) {
      return { background: 'linear-gradient(135deg, #0d0520 0%, #1a0d33 50%, #0d0520 100%)' };
    } else if (condition.includes('cloud')) {
      return { background: isDay ? 'linear-gradient(135deg, #3d4a5c 0%, #5a6a7f 50%, #3d4a5c 100%)' : 'linear-gradient(135deg, #0a0e14 0%, #151d28 50%, #0a0e14 100%)' };
    } else if (condition.includes('clear') || condition.includes('sun')) {
      return { background: isDay ? 'linear-gradient(135deg, #e67e22 0%, #f39c12 30%, #f4c430 50%, #f39c12 70%, #e67e22 100%)' : currentTheme.background };
    }

    return { background: currentTheme.background };
  };

  const fetchWeather = async (loc = location) => {
    if (!loc.trim()) {
      setError('Please enter a location');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const currentResponse = await fetch(`/api/weather/${encodeURIComponent(loc)}`);
      
      if (!currentResponse.ok) {
        throw new Error('Location not found');
      }
      
      const currentData = await currentResponse.json();
      setWeather(currentData);

      const forecastResponse = await fetch(`/api/forecast/${encodeURIComponent(loc)}?days=7`);
      
      if (forecastResponse.ok) {
        const forecastData = await forecastResponse.json();
        setForecast(forecastData);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch weather data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchWeather();
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported');
      return;
    }

    setLoading(true);
    setError('');
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const loc = `${latitude},${longitude}`;
          setLocation(loc);
          await fetchWeather(loc);
        } catch (err) {
          setError('Failed to get weather for your location');
          setLoading(false);
        }
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location. ';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please allow location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Request timed out.';
            break;
          default:
            errorMessage += 'Unknown error.';
        }
        setError(errorMessage);
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const addToFavorites = () => {
    if (!weather) return;
    const newFavorite = {
      name: weather.location.name,
      region: weather.location.region,
      country: weather.location.country
    };
    const exists = favorites.some(fav => fav.name === newFavorite.name);
    if (!exists) {
      const updated = [...favorites, newFavorite];
      setFavorites(updated);
      localStorage.setItem('weatherFavorites', JSON.stringify(updated));
    }
  };

  const removeFromFavorites = (name) => {
    const updated = favorites.filter(fav => fav.name !== name);
    setFavorites(updated);
    localStorage.setItem('weatherFavorites', JSON.stringify(updated));
  };

  const loadFavorite = (fav) => {
    setLocation(fav.name);
    fetchWeather(fav.name);
  };

  // Due to file size limits, I'll continue this in the style section and render...
  
  const moonPhase = getMoonPhase(currentTime);

  return (
    <div className="weather-app" style={getBackgroundStyle()}>
      <canvas ref={canvasRef} className="particle-canvas"></canvas>
      <audio ref={audioRef} style={{ display: 'none' }} />
      
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@300;400;600;700&display=swap');
        
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { overflow-x: hidden; }

        .weather-app {
          min-height: 100vh;
          color: ${currentTheme.textPrimary};
          font-family: 'Rajdhani', sans-serif;
          position: relative;
          overflow: hidden;
          transition: background 1s ease;
        }

        .particle-canvas {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }

        .scan-lines {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: repeating-linear-gradient(0deg, rgba(0, 255, 255, 0.03) 0px, rgba(0, 255, 255, 0.03) 1px, transparent 1px, transparent 2px);
          pointer-events: none;
          z-index: 1;
          animation: scanMove 8s linear infinite;
        }

        @keyframes scanMove {
          0% { transform: translateY(0); }
          100% { transform: translateY(20px); }
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
          position: relative;
          z-index: 2;
        }

        .header {
          text-align: center;
          margin-bottom: 40px;
          animation: fadeInDown 1s ease-out;
          position: relative;
        }

        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .title {
          font-family: 'Orbitron', monospace;
          font-size: 4rem;
          font-weight: 900;
          letter-spacing: 0.1em;
          background: linear-gradient(135deg, ${currentTheme.primary} 0%, ${currentTheme.secondary} 50%, ${currentTheme.primary} 100%);
          background-size: 200% 200%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 3s ease infinite;
          margin-bottom: 10px;
          filter: brightness(1.3);
        }

        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        .subtitle {
          font-size: 1.1rem;
          color: ${currentTheme.textSecondary};
          letter-spacing: 0.3em;
          text-transform: uppercase;
          animation: fadeIn 1s ease-out 0.3s both;
          filter: brightness(1.4);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .settings-button {
          position: absolute;
          top: 0;
          right: 20px;
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(10px);
          border: 2px solid ${currentTheme.primary}40;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          color: ${currentTheme.primary};
        }

        .settings-button:hover {
          background: ${currentTheme.primary}20;
          border-color: ${currentTheme.primary};
          transform: rotate(90deg);
        }

        .install-banner {
          background: linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.secondary});
          color: #000;
          padding: 15px 30px;
          border-radius: 10px;
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          animation: slideInDown 0.5s ease-out;
          font-weight: 600;
        }

        @keyframes slideInDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .install-button {
          background: #000;
          color: ${currentTheme.primary};
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: transform 0.2s;
        }

        .install-button:hover {
          transform: scale(1.05);
        }

        .clock {
          text-align: center;
          font-family: 'Orbitron', monospace;
          font-size: 1.5rem;
          color: ${currentTheme.textPrimary};
          margin-bottom: 30px;
          padding: 15px;
          border: 2px solid ${currentTheme.primary}80;
          border-radius: 10px;
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(10px);
          animation: pulse 2s ease-in-out infinite;
          filter: brightness(1.3);
        }

        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 10px ${currentTheme.primary}40; }
          50% { box-shadow: 0 0 20px ${currentTheme.primary}80; }
        }

        .moon-phase {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 15px;
          margin-bottom: 30px;
          padding: 15px;
          background: ${currentTheme.cardBg};
          border: 2px solid ${currentTheme.primary}40;
          border-radius: 10px;
          backdrop-filter: blur(10px);
          animation: fadeIn 1s ease-out 0.4s both;
        }

        .moon-emoji {
          font-size: 3rem;
          animation: float 3s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        .moon-name {
          font-family: 'Orbitron', monospace;
          font-size: 1.2rem;
          color: ${currentTheme.textPrimary};
          font-weight: 700;
        }

        .alerts-container {
          margin-bottom: 30px;
          animation: fadeIn 1s ease-out 0.4s both;
        }

        .alert {
          padding: 15px 20px;
          border-radius: 10px;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 15px;
          font-size: 1rem;
          font-weight: 600;
          backdrop-filter: blur(10px);
          animation: slideInLeft 0.5s ease-out;
        }

        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .alert.warning {
          background: rgba(255, 165, 0, 0.2);
          border: 2px solid rgba(255, 165, 0, 0.5);
          color: #ffb347;
        }

        .alert.danger {
          background: rgba(255, 0, 0, 0.2);
          border: 2px solid rgba(255, 0, 0, 0.5);
          color: #ff6b6b;
          animation: alertPulse 2s ease-in-out infinite;
        }

        @keyframes alertPulse {
          0%, 100% { box-shadow: 0 0 10px rgba(255, 0, 0, 0.3); }
          50% { box-shadow: 0 0 20px rgba(255, 0, 0, 0.6); }
        }

        .search-form {
          margin-bottom: 30px;
          animation: fadeIn 1s ease-out 0.5s both;
        }

        .input-group {
          display: flex;
          gap: 15px;
          max-width: 700px;
          margin: 0 auto;
          flex-wrap: wrap;
        }

        .search-input {
          flex: 1;
          min-width: 250px;
          padding: 18px 25px;
          font-size: 1.1rem;
          font-family: 'Rajdhani', sans-serif;
          font-weight: 600;
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(10px);
          border: 2px solid ${currentTheme.primary}60;
          border-radius: 10px;
          color: ${currentTheme.textPrimary};
          outline: none;
          transition: all 0.3s ease;
        }

        .search-input::placeholder {
          color: ${currentTheme.textPrimary}80;
        }

        .search-input:focus {
          border-color: ${currentTheme.primary};
          box-shadow: 0 0 20px ${currentTheme.primary}60;
          background: ${currentTheme.primary}20;
        }

        .search-button, .location-button {
          padding: 18px 30px;
          font-size: 1.1rem;
          font-family: 'Orbitron', monospace;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          background: linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.secondary});
          border: none;
          border-radius: 10px;
          color: #000;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          filter: brightness(1.2);
        }

        .location-button {
          background: linear-gradient(135deg, ${currentTheme.secondary}, ${currentTheme.accent});
        }

        .search-button:hover, .location-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px ${currentTheme.primary}60;
        }

        .favorites-section {
          margin-bottom: 30px;
          animation: fadeIn 1s ease-out 0.6s both;
        }

        .favorites-grid {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
          margin-top: 15px;
        }

        .favorite-chip {
          padding: 10px 20px;
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(10px);
          border: 2px solid ${currentTheme.primary}40;
          border-radius: 25px;
          color: ${currentTheme.textPrimary};
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .favorite-chip:hover {
          background: ${currentTheme.primary}20;
          border-color: ${currentTheme.primary};
          transform: translateY(-2px);
        }

        .favorite-chip button {
          background: none;
          border: none;
          color: #ff0080;
          cursor: pointer;
          padding: 0;
          margin-left: 5px;
          font-size: 1.2rem;
          transition: transform 0.2s;
        }

        .favorite-chip button:hover {
          transform: scale(1.3);
        }

        .view-tabs {
          display: flex;
          gap: 10px;
          justify-content: center;
          margin-bottom: 30px;
          animation: fadeIn 1s ease-out 0.7s both;
          flex-wrap: wrap;
        }

        .tab-button {
          padding: 12px 30px;
          font-family: 'Orbitron', monospace;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(10px);
          border: 2px solid ${currentTheme.primary}40;
          border-radius: 8px;
          color: ${currentTheme.textPrimary};
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tab-button:hover {
          background: ${currentTheme.primary}20;
          transform: translateY(-2px);
        }

        .tab-button.active {
          background: linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.secondary});
          color: #000;
          border-color: transparent;
          box-shadow: 0 5px 20px ${currentTheme.primary}60;
        }

        .error-message {
          text-align: center;
          color: #ff0080;
          font-size: 1.1rem;
          margin-top: 20px;
          padding: 15px;
          border: 2px solid rgba(255, 0, 128, 0.3);
          border-radius: 10px;
          background: rgba(255, 0, 128, 0.1);
          backdrop-filter: blur(10px);
          animation: shake 0.5s ease;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }

        .weather-card {
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(20px);
          border: 2px solid ${currentTheme.primary}60;
          border-radius: 20px;
          padding: 40px;
          position: relative;
          overflow: hidden;
          animation: slideUp 0.8s ease-out;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(50px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .location-info {
          text-align: center;
          margin-bottom: 30px;
          animation: fadeIn 1s ease-out 0.2s both;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 15px;
          flex-wrap: wrap;
        }

        .location-name {
          font-family: 'Orbitron', monospace;
          font-size: 2.5rem;
          font-weight: 700;
          color: ${currentTheme.textPrimary};
          text-shadow: 0 0 30px ${currentTheme.primary}CC;
          filter: brightness(1.3);
        }

        .favorite-button {
          background: none;
          border: 2px solid ${currentTheme.primary}40;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          color: ${currentTheme.textPrimary};
        }

        .favorite-button:hover {
          background: ${currentTheme.primary}20;
          border-color: ${currentTheme.primary};
          transform: scale(1.1);
        }

        .favorite-button.active {
          color: #ff0080;
          border-color: #ff0080;
        }

        .location-region {
          font-size: 1.3rem;
          color: ${currentTheme.textSecondary};
          letter-spacing: 0.2em;
          width: 100%;
          text-align: center;
        }

        .main-weather {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 40px;
          margin: 40px 0;
          animation: fadeIn 1s ease-out 0.4s both;
        }

        .weather-icon {
          width: 120px;
          height: 120px;
          color: ${currentTheme.textPrimary};
          filter: drop-shadow(0 0 20px ${currentTheme.primary}99);
          animation: float 3s ease-in-out infinite;
        }

        .temperature-display {
          text-align: center;
        }

        .temperature {
          font-family: 'Orbitron', monospace;
          font-size: 5rem;
          font-weight: 900;
          background: linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.accent});
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          line-height: 1;
          margin-bottom: 10px;
          filter: brightness(1.4) contrast(1.2);
        }

        .condition {
          font-size: 1.5rem;
          color: ${currentTheme.textSecondary};
          text-transform: uppercase;
          letter-spacing: 0.2em;
        }

        .sun-times {
          display: flex;
          justify-content: center;
          gap: 40px;
          margin: 30px 0;
          animation: fadeIn 1s ease-out 0.5s both;
        }

        .sun-time {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 15px 25px;
          background: ${currentTheme.cardBg};
          border: 1px solid ${currentTheme.primary}40;
          border-radius: 10px;
          backdrop-filter: blur(10px);
        }

        .sun-time-icon {
          color: #ffb347;
        }

        .sun-time-label {
          font-size: 0.9rem;
          color: ${currentTheme.textPrimary}99;
          text-transform: uppercase;
        }

        .sun-time-value {
          font-family: 'Orbitron', monospace;
          font-size: 1.2rem;
          color: ${currentTheme.textPrimary};
          font-weight: 700;
        }

        .weather-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-top: 40px;
          animation: fadeIn 1s ease-out 0.6s both;
        }

        .detail-card {
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(10px);
          border: 1px solid ${currentTheme.primary}40;
          border-radius: 15px;
          padding: 25px;
          text-align: center;
          transition: all 0.3s ease;
          animation: cardAppear 0.5s ease-out both;
        }

        .detail-card:nth-child(1) { animation-delay: 0.1s; }
        .detail-card:nth-child(2) { animation-delay: 0.2s; }
        .detail-card:nth-child(3) { animation-delay: 0.3s; }
        .detail-card:nth-child(4) { animation-delay: 0.4s; }
        .detail-card:nth-child(5) { animation-delay: 0.5s; }
        .detail-card:nth-child(6) { animation-delay: 0.6s; }

        @keyframes cardAppear {
          from { opacity: 0; transform: scale(0.8) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        .detail-card:hover {
          background: ${currentTheme.primary}20;
          border-color: ${currentTheme.primary}66;
          transform: translateY(-5px) scale(1.05);
          box-shadow: 0 10px 25px ${currentTheme.primary}40;
        }

        .detail-icon {
          width: 40px;
          height: 40px;
          color: ${currentTheme.textSecondary};
          margin: 0 auto 15px;
        }

        .detail-label {
          font-size: 0.9rem;
          color: ${currentTheme.textPrimary}99;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          margin-bottom: 10px;
        }

        .detail-value {
          font-family: 'Orbitron', monospace;
          font-size: 1.8rem;
          font-weight: 700;
          color: ${currentTheme.textPrimary};
          filter: brightness(1.3);
        }

        .chart-container {
          margin: 40px 0;
          padding: 30px;
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(10px);
          border: 2px solid ${currentTheme.primary}40;
          border-radius: 20px;
          animation: fadeIn 1s ease-out 0.8s both;
        }

        .chart-title {
          font-family: 'Orbitron', monospace;
          font-size: 1.5rem;
          color: ${currentTheme.textPrimary};
          margin-bottom: 25px;
          text-align: center;
          text-transform: uppercase;
          letter-spacing: 0.2em;
        }

        .temperature-chart {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          height: 200px;
          gap: 10px;
          padding: 20px 0;
          border-bottom: 2px solid ${currentTheme.primary}40;
        }

        .chart-bar {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .bar {
          width: 100%;
          background: linear-gradient(180deg, ${currentTheme.accent} 0%, ${currentTheme.primary} 100%);
          border-radius: 10px 10px 0 0;
          transition: all 0.3s ease;
          position: relative;
          animation: barGrow 1s ease-out both;
        }

        @keyframes barGrow {
          from { height: 0 !important; opacity: 0; }
          to { opacity: 1; }
        }

        .chart-bar:nth-child(1) .bar { animation-delay: 0.1s; }
        .chart-bar:nth-child(2) .bar { animation-delay: 0.2s; }
        .chart-bar:nth-child(3) .bar { animation-delay: 0.3s; }
        .chart-bar:nth-child(4) .bar { animation-delay: 0.4s; }
        .chart-bar:nth-child(5) .bar { animation-delay: 0.5s; }
        .chart-bar:nth-child(6) .bar { animation-delay: 0.6s; }
        .chart-bar:nth-child(7) .bar { animation-delay: 0.7s; }
        .chart-bar:nth-child(8) .bar { animation-delay: 0.8s; }

        .bar:hover {
          filter: brightness(1.3);
          transform: scaleY(1.05);
        }

        .bar-label {
          font-family: 'Orbitron', monospace;
          font-size: 0.9rem;
          color: ${currentTheme.textPrimary};
          font-weight: 700;
        }

        .bar-time {
          font-size: 0.8rem;
          color: ${currentTheme.textSecondary};
          margin-top: 10px;
        }

        .rain-chart {
          margin-top: 40px;
        }

        .rain-bars {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          height: 150px;
          gap: 8px;
          padding: 20px 0;
          border-bottom: 2px solid ${currentTheme.primary}40;
        }

        .rain-bar {
          flex: 1;
          background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%);
          border-radius: 8px 8px 0 0;
          transition: all 0.3s ease;
          animation: barGrow 1s ease-out both;
        }

        .rain-bar:hover {
          filter: brightness(1.3);
        }

        .rain-label {
          font-size: 0.8rem;
          color: ${currentTheme.textPrimary}CC;
          margin-top: 10px;
          text-align: center;
        }

        .hourly-forecast, .daily-forecast {
          animation: fadeIn 0.8s ease-out;
        }

        .hourly-forecast {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 15px;
        }

        .hourly-card {
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(10px);
          border: 1px solid ${currentTheme.primary}40;
          border-radius: 15px;
          padding: 20px;
          text-align: center;
          transition: all 0.3s ease;
          animation: slideUp 0.5s ease-out both;
        }

        .hourly-card:nth-child(1) { animation-delay: 0.1s; }
        .hourly-card:nth-child(2) { animation-delay: 0.15s; }
        .hourly-card:nth-child(3) { animation-delay: 0.2s; }
        .hourly-card:nth-child(4) { animation-delay: 0.25s; }
        .hourly-card:nth-child(5) { animation-delay: 0.3s; }
        .hourly-card:nth-child(6) { animation-delay: 0.35s; }
        .hourly-card:nth-child(7) { animation-delay: 0.4s; }
        .hourly-card:nth-child(8) { animation-delay: 0.45s; }

        .hourly-card:hover {
          background: ${currentTheme.primary}20;
          border-color: ${currentTheme.primary}66;
          transform: translateY(-5px);
          box-shadow: 0 8px 20px ${currentTheme.primary}40;
        }

        .hourly-time {
          font-family: 'Orbitron', monospace;
          font-size: 1rem;
          color: ${currentTheme.textSecondary};
          margin-bottom: 15px;
        }

        .hourly-icon {
          color: ${currentTheme.textPrimary};
          margin: 10px 0;
          filter: drop-shadow(0 0 10px ${currentTheme.primary}66);
        }

        .hourly-temp {
          font-family: 'Orbitron', monospace;
          font-size: 1.5rem;
          font-weight: 700;
          color: ${currentTheme.textPrimary};
          margin-top: 10px;
        }

        .daily-forecast {
          display: grid;
          gap: 20px;
        }

        .daily-card {
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(10px);
          border: 1px solid ${currentTheme.primary}40;
          border-radius: 15px;
          padding: 25px;
          display: grid;
          grid-template-columns: 150px 1fr 150px;
          align-items: center;
          gap: 30px;
          transition: all 0.3s ease;
          animation: slideInRight 0.6s ease-out both;
        }

        .daily-card:nth-child(1) { animation-delay: 0.1s; }
        .daily-card:nth-child(2) { animation-delay: 0.2s; }
        .daily-card:nth-child(3) { animation-delay: 0.3s; }
        .daily-card:nth-child(4) { animation-delay: 0.4s; }
        .daily-card:nth-child(5) { animation-delay: 0.5s; }
        .daily-card:nth-child(6) { animation-delay: 0.6s; }
        .daily-card:nth-child(7) { animation-delay: 0.7s; }

        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .daily-card:hover {
          background: ${currentTheme.primary}20;
          border-color: ${currentTheme.primary}66;
          transform: translateX(10px);
          box-shadow: 0 8px 30px ${currentTheme.primary}40;
        }

        .daily-date {
          font-family: 'Orbitron', monospace;
          font-size: 1.2rem;
          color: ${currentTheme.textPrimary};
        }

        .daily-day {
          font-size: 1rem;
          color: ${currentTheme.textSecondary};
          margin-top: 5px;
        }

        .daily-info {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .daily-info-icon {
          color: ${currentTheme.textPrimary};
          filter: drop-shadow(0 0 10px ${currentTheme.primary}66);
        }

        .daily-condition {
          font-size: 1.1rem;
          color: ${currentTheme.textSecondary};
        }

        .daily-temps {
          display: flex;
          gap: 20px;
          justify-content: flex-end;
          font-family: 'Orbitron', monospace;
          font-size: 1.5rem;
        }

        .temp-high {
          color: ${currentTheme.accent};
          font-weight: 700;
        }

        .temp-low {
          color: ${currentTheme.primary};
        }

        .loading {
          text-align: center;
          padding: 60px 20px;
        }

        .spinner {
          width: 60px;
          height: 60px;
          border: 4px solid ${currentTheme.primary}40;
          border-top-color: ${currentTheme.primary};
          border-radius: 50%;
          margin: 0 auto 20px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-text {
          font-family: 'Orbitron', monospace;
          font-size: 1.3rem;
          color: ${currentTheme.textPrimary};
          letter-spacing: 0.2em;
          animation: pulse 1.5s ease-in-out infinite;
        }

        .settings-modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(10px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: fadeIn 0.3s ease-out;
        }

        .settings-content {
          background: ${currentTheme.cardBg};
          backdrop-filter: blur(20px);
          border: 2px solid ${currentTheme.primary}60;
          border-radius: 20px;
          padding: 40px;
          max-width: 500px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          animation: slideUp 0.3s ease-out;
        }

        .settings-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
        }

        .settings-title {
          font-family: 'Orbitron', monospace;
          font-size: 2rem;
          color: ${currentTheme.textPrimary};
        }

        .close-button {
          background: none;
          border: none;
          color: ${currentTheme.textPrimary};
          cursor: pointer;
          padding: 10px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .close-button:hover {
          background: ${currentTheme.primary}20;
          transform: rotate(90deg);
        }

        .setting-group {
          margin-bottom: 30px;
        }

        .setting-label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 1.1rem;
          color: ${currentTheme.textPrimary};
          margin-bottom: 15px;
          font-weight: 600;
        }

        .theme-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
        }

        .theme-button {
          padding: 20px;
          border: 2px solid ${currentTheme.primary}40;
          border-radius: 10px;
          background: ${currentTheme.cardBg};
          color: ${currentTheme.textPrimary};
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .theme-button:hover {
          border-color: ${currentTheme.primary};
          transform: translateY(-2px);
        }

        .theme-button.active {
          background: linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.secondary});
          color: #000;
          border-color: transparent;
        }

        .language-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
        }

        .language-button {
          padding: 15px;
          border: 2px solid ${currentTheme.primary}40;
          border-radius: 10px;
          background: ${currentTheme.cardBg};
          color: ${currentTheme.textPrimary};
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 1rem;
          font-weight: 600;
        }

        .language-button:hover {
          border-color: ${currentTheme.primary};
          transform: translateY(-2px);
        }

        .language-button.active {
          background: linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.secondary});
          color: #000;
          border-color: transparent;
        }

        .toggle-group {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: ${currentTheme.cardBg};
          border: 2px solid ${currentTheme.primary}40;
          border-radius: 10px;
          margin-bottom: 15px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .toggle-group:hover {
          border-color: ${currentTheme.primary};
        }

        .toggle-group.active {
          border-color: ${currentTheme.primary};
          background: ${currentTheme.primary}20;
        }

        .toggle-icon {
          color: ${currentTheme.primary};
        }

        @media (max-width: 768px) {
          .title { font-size: 2.5rem; }
          .main-weather { flex-direction: column; gap: 20px; }
          .temperature { font-size: 3.5rem; }
          .weather-details, .hourly-forecast { grid-template-columns: 1fr; }
          .input-group { flex-direction: column; }
          .daily-card { grid-template-columns: 1fr; gap: 15px; text-align: center; }
          .daily-temps { justify-content: center; }
          .view-tabs, .favorites-grid { flex-wrap: wrap; }
          .sun-times { flex-direction: column; align-items: center; }
          .temperature-chart, .rain-bars { height: 150px; }
          .theme-grid, .language-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="scan-lines"></div>
      
      <div className="container">
        <div className="header">
          <h1 className="title">{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <button className="settings-button" onClick={() => setShowSettings(true)}>
            <SettingsIcon size={24} />
          </button>
        </div>

        {showInstall && (
          <div className="install-banner">
            <span>📱 {t.installApp}</span>
            <button className="install-button" onClick={installPWA}>
              <Download size={20} />
              {t.installApp}
            </button>
          </div>
        )}

        <div className="clock">
          {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })} UTC
        </div>

        <div className="moon-phase">
          <span className="moon-emoji">{moonPhase.emoji}</span>
          <div>
            <div className="moon-name">{moonPhase.name}</div>
          </div>
        </div>

        {alerts.length > 0 && (
          <div className="alerts-container">
            {alerts.map((alert, index) => (
              <div key={index} className={`alert ${alert.type}`}>
                {alert.icon}
                <span>{alert.message}</span>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="search-form">
          <div className="input-group">
            <input
              type="text"
              className="search-input"
              placeholder={t.enterLocation}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              disabled={loading}
            />
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? t.searching : t.search}
            </button>
            <button type="button" className="location-button" onClick={getCurrentLocation} disabled={loading}>
              <MapPin size={18} />
              {t.myLocation}
            </button>
          </div>
        </form>

        {favorites.length > 0 && (
          <div className="favorites-section">
            <div className="favorites-grid">
              {favorites.map((fav, index) => (
                <div key={index} className="favorite-chip" onClick={() => loadFavorite(fav)}>
                  <Heart size={16} fill="#ff0080" />
                  {fav.name}
                  <button onClick={(e) => { e.stopPropagation(); removeFromFavorites(fav.name); }}>×</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            ⚠ {error}
          </div>
        )}

        {loading && (
          <div className="loading">
            <div className="spinner"></div>
            <p className="loading-text">{t.analyzing}</p>
          </div>
        )}

        {weather && !loading && (
          <>
            <div className="view-tabs">
              <button className={`tab-button ${viewMode === 'current' ? 'active' : ''}`} onClick={() => setViewMode('current')}>
                <TrendingUp size={18} />
                {t.current}
              </button>
              <button className={`tab-button ${viewMode === 'hourly' ? 'active' : ''}`} onClick={() => setViewMode('hourly')} disabled={!forecast}>
                <Clock size={18} />
                {t.hourly}
              </button>
              <button className={`tab-button ${viewMode === 'daily' ? 'active' : ''}`} onClick={() => setViewMode('daily')} disabled={!forecast}>
                <Calendar size={18} />
                {t.daily}
              </button>
            </div>

            {viewMode === 'current' && (
              <div className="weather-card">
                <div className="location-info">
                  <h2 className="location-name">{weather.location.name}</h2>
                  <button 
                    className={`favorite-button ${favorites.some(f => f.name === weather.location.name) ? 'active' : ''}`}
                    onClick={addToFavorites}
                  >
                    <Heart size={24} fill={favorites.some(f => f.name === weather.location.name) ? '#ff0080' : 'none'} />
                  </button>
                  <p className="location-region">
                    {weather.location.region}, {weather.location.country}
                  </p>
                </div>

                <div className="main-weather">
                  {getWeatherIcon(weather.current.condition.text)}
                  <div className="temperature-display">
                    <div className="temperature">{Math.round(weather.current.temp_c)}°</div>
                    <div className="condition">{weather.current.condition.text}</div>
                  </div>
                </div>

                {forecast && forecast.forecast.forecastday[0].astro && (
                  <div className="sun-times">
                    <div className="sun-time">
                      <Sunrise className="sun-time-icon" size={24} />
                      <div>
                        <div className="sun-time-label">{t.sunrise}</div>
                        <div className="sun-time-value">{forecast.forecast.forecastday[0].astro.sunrise}</div>
                      </div>
                    </div>
                    <div className="sun-time">
                      <Sunset className="sun-time-icon" size={24} />
                      <div>
                        <div className="sun-time-label">{t.sunset}</div>
                        <div className="sun-time-value">{forecast.forecast.forecastday[0].astro.sunset}</div>
                      </div>
                    </div>
                  </div>
                )}

                {forecast && (
                  <>
                    <div className="chart-container">
                      <div className="chart-title">{t.tempTrend}</div>
                      <div className="temperature-chart">
                        {forecast.forecast.forecastday[0].hour
                          .filter((_, index) => index % 3 === 0)
                          .slice(0, 8)
                          .map((hour, index) => {
                            const maxTemp = Math.max(...forecast.forecast.forecastday[0].hour.map(h => h.temp_c));
                            const minTemp = Math.min(...forecast.forecast.forecastday[0].hour.map(h => h.temp_c));
                            const range = maxTemp - minTemp || 1;
                            const height = ((hour.temp_c - minTemp) / range) * 150 + 30;
                            
                            return (
                              <div key={index} className="chart-bar">
                                <div className="bar-label">{Math.round(hour.temp_c)}°</div>
                                <div className="bar" style={{ height: `${height}px` }}></div>
                                <div className="bar-time">
                                  {new Date(hour.time).toLocaleTimeString('en-US', { hour: '2-digit', hour12: false })}:00
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    <div className="chart-container rain-chart">
                      <div className="chart-title">{t.rainChance}</div>
                      <div className="rain-bars">
                        {forecast.forecast.forecastday[0].hour
                          .filter((_, index) => index % 3 === 0)
                          .slice(0, 8)
                          .map((hour, index) => {
                            const height = (hour.chance_of_rain / 100) * 130 + 20;
                            return (
                              <div key={index} style={{ flex: 1, textAlign: 'center' }}>
                                <div className="rain-bar" style={{ height: `${height}px` }}></div>
                                <div className="rain-label">{hour.chance_of_rain}%</div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </>
                )}

                <div className="weather-details">
                  <div className="detail-card">
                    <Droplets className="detail-icon" />
                    <div className="detail-label">{t.humidity}</div>
                    <div className="detail-value">{weather.current.humidity}%</div>
                  </div>
                  <div className="detail-card">
                    <Wind className="detail-icon" />
                    <div className="detail-label">{t.windSpeed}</div>
                    <div className="detail-value">{weather.current.wind_kph} km/h</div>
                  </div>
                  <div className="detail-card">
                    <Eye className="detail-icon" />
                    <div className="detail-label">{t.visibility}</div>
                    <div className="detail-value">{weather.current.vis_km} km</div>
                  </div>
                  <div className="detail-card">
                    <Gauge className="detail-icon" />
                    <div className="detail-label">{t.pressure}</div>
                    <div className="detail-value">{weather.current.pressure_mb} mb</div>
                  </div>
                  <div className="detail-card">
                    <Sun className="detail-icon" />
                    <div className="detail-label">{t.uvIndex}</div>
                    <div className="detail-value">{weather.current.uv}</div>
                  </div>
                  <div className="detail-card">
                    <Cloud className="detail-icon" />
                    <div className="detail-label">{t.feelsLike}</div>
                    <div className="detail-value">{Math.round(weather.current.feelslike_c)}°</div>
                  </div>
                </div>
              </div>
            )}

            {viewMode === 'hourly' && forecast && (
              <div className="weather-card">
                <div className="location-info">
                  <h2 className="location-name">{t.hourly} {t.title}</h2>
                </div>
                <div className="hourly-forecast">
                  {forecast.forecast.forecastday[0].hour.slice(new Date().getHours(), new Date().getHours() + 8).map((hour, index) => (
                    <div key={index} className="hourly-card">
                      <div className="hourly-time">
                        {new Date(hour.time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                      </div>
                      <div className="hourly-icon">{getSmallWeatherIcon(hour.condition.text)}</div>
                      <div className="hourly-temp">{Math.round(hour.temp_c)}°</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {viewMode === 'daily' && forecast && (
              <div className="weather-card">
                <div className="location-info">
                  <h2 className="location-name">{t.daily} {t.title}</h2>
                </div>
                <div className="daily-forecast">
                  {forecast.forecast.forecastday.map((day, index) => (
                    <div key={index} className="daily-card">
                      <div>
                        <div className="daily-date">
                          {new Date(day.date).toLocaleDateString(language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : language === 'fr' ? 'fr-FR' : 'de-DE', { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="daily-day">
                          {new Date(day.date).toLocaleDateString(language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : language === 'fr' ? 'fr-FR' : 'de-DE', { weekday: 'long' })}
                        </div>
                      </div>
                      <div className="daily-info">
                        <div className="daily-info-icon">{getSmallWeatherIcon(day.day.condition.text)}</div>
                        <div className="daily-condition">{day.day.condition.text}</div>
                      </div>
                      <div className="daily-temps">
                        <span className="temp-high">{Math.round(day.day.maxtemp_c)}°</span>
                        <span className="temp-low">{Math.round(day.day.mintemp_c)}°</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showSettings && (
        <div className="settings-modal" onClick={() => setShowSettings(false)}>
          <div className="settings-content" onClick={(e) => e.stopPropagation()}>
            <div className="settings-header">
              <h2 className="settings-title">{t.settings}</h2>
              <button className="close-button" onClick={() => setShowSettings(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="setting-group">
              <div className="setting-label">
                <Palette size={20} />
                {t.theme}
              </div>
              <div className="theme-grid">
                {Object.keys(themes).map(themeName => (
                  <button
                    key={themeName}
                    className={`theme-button ${theme === themeName ? 'active' : ''}`}
                    onClick={() => setTheme(themeName)}
                  >
                    {themeName}
                  </button>
                ))}
              </div>
            </div>

            <div className="setting-group">
              <div className="setting-label">
                <Globe size={20} />
                {t.language}
              </div>
              <div className="language-grid">
                <button className={`language-button ${language === 'en' ? 'active' : ''}`} onClick={() => setLanguage('en')}>
                  🇬🇧 English
                </button>
                <button className={`language-button ${language === 'es' ? 'active' : ''}`} onClick={() => setLanguage('es')}>
                  🇪🇸 Español
                </button>
                <button className={`language-button ${language === 'fr' ? 'active' : ''}`} onClick={() => setLanguage('fr')}>
                  🇫🇷 Français
                </button>
                <button className={`language-button ${language === 'de' ? 'active' : ''}`} onClick={() => setLanguage('de')}>
                  🇩🇪 Deutsch
                </button>
              </div>
            </div>

            <div className="setting-group">
              <div 
                className={`toggle-group ${soundEnabled ? 'active' : ''}`}
                onClick={() => setSoundEnabled(!soundEnabled)}
              >
                <div className="setting-label" style={{ marginBottom: 0 }}>
                  {soundEnabled ? <Volume2 size={20} className="toggle-icon" /> : <VolumeX size={20} className="toggle-icon" />}
                  {t.sound}
                </div>
                <div style={{ color: soundEnabled ? currentTheme.primary : currentTheme.textSecondary }}>
                  {soundEnabled ? 'ON' : 'OFF'}
                </div>
              </div>

              <div 
                className={`toggle-group ${notificationsEnabled ? 'active' : ''}`}
                onClick={toggleNotifications}
              >
                <div className="setting-label" style={{ marginBottom: 0 }}>
                  {notificationsEnabled ? <Bell size={20} className="toggle-icon" /> : <BellOff size={20} className="toggle-icon" />}
                  {t.notifications}
                </div>
                <div style={{ color: notificationsEnabled ? currentTheme.primary : currentTheme.textSecondary }}>
                  {notificationsEnabled ? 'ON' : 'OFF'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
